import type { ReactNode } from 'react'
import { BookOpen, Flame, TrendingUp } from 'lucide-react'
import { FaInstagram, FaYoutube, FaTiktok, FaXTwitter, FaFacebook } from 'react-icons/fa6'

// The page a brand sees when a manager shares a talent sheet. The manager app renders it as a
// preview; the marketer app renders it for brands, with messaging around it.

export interface PublicSocialAccount {
  platform: string
  followers: number | null
  /** A fraction: 0.0155 = 1.55% */
  engagementRate?: number | null
  isPrimary: boolean
}

export interface PublicTalentSheetAthlete {
  id: string
  name: string
  avatar: string | null
  school: string | null
  socialAccounts: PublicSocialAccount[]
  /** Followers across all their accounts */
  totalReach?: number | null
  /** Follower-weighted engagement across their accounts, as a fraction */
  engagementRate?: number | null
}

export interface PublicTalentSheet {
  id: string
  title: string
  brandName: string | null
  /** The brand-facing link to this sheet, in the marketer app */
  shareUrl: string
  manager: { name: string }
  teamHashId: string
  athletes: PublicTalentSheetAthlete[]
}

/**
 * Two looks: 'dark' (the manager app) and 'brand' (the brand app's light theme). The brand look
 * uses fixed hex colours so it renders the same whatever the app does to the Tailwind palette.
 */
export type SheetVariant = 'dark' | 'brand'

const STYLES = {
  dark: {
    page: 'min-h-screen bg-gradient-to-br from-[#000000] via-[#0a1e29] to-[#003b4d]',
    topBar: 'bg-gray-100 border-foreground/10',
    header: 'border-foreground/10',
    icon: 'text-[#9CF0FB]',
    title: 'text-foreground',
    muted: 'text-gray-500',
    empty: 'text-gray-600',
    card: 'bg-card border-foreground/10 hover:bg-foreground/10 hover:border-foreground/20',
    avatar: 'border-foreground/10 group-hover:border-[#9CF0FB]/40',
    avatarFallback: 'bg-muted text-foreground',
    name: 'text-foreground group-hover:text-[#9CF0FB]',
    chip: 'bg-card border-foreground/10 text-gray-700',
    spinner: 'border-[#9CF0FB]',
    footer: 'text-gray-400',
  },
  brand: {
    page: 'min-h-screen bg-[#F6F8F3]',
    topBar: 'bg-[#FFFFFF] border-[#E3E7DE]',
    header: 'border-[#E3E7DE] bg-[#FFFFFF]',
    icon: 'text-[#3F7A0A]',
    title: 'text-[#111827]',
    muted: 'text-[#6B7280]',
    empty: 'text-[#6B7280]',
    card: 'bg-[#FFFFFF] border-[#E3E7DE] shadow-[0_1px_2px_rgba(17,24,39,0.04)] hover:border-[#9BD84F] hover:shadow-md',
    avatar: 'border-[#E3E7DE] group-hover:border-[#9BD84F]',
    avatarFallback: 'bg-[#EEF1EA] text-[#111827]',
    name: 'text-[#111827] group-hover:text-[#3F7A0A]',
    chip: 'bg-[#F1F4EE] border-[#E3E7DE] text-[#374151]',
    spinner: 'border-[#7CB518]',
    footer: 'text-[#9CA3AF]',
  },
} satisfies Record<SheetVariant, Record<string, string>>

/** Instagram and TikTok in their own colours, as brands know them */
const PlatformMark = ({ platform }: { platform: string }) => {
  if (platform === 'instagram')
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)] text-[#FFFFFF]">
        <FaInstagram className="h-5 w-5" />
      </span>
    )
  if (platform === 'tiktok')
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[#111827]">
        <FaTiktok className="h-6 w-6 [filter:drop-shadow(-1.5px_-1.5px_0_#25F4EE)_drop-shadow(1.5px_1.5px_0_#FE2C55)]" />
      </span>
    )
  if (platform === 'youtube')
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[#FF0000]">
        <FaYoutube className="h-7 w-7" />
      </span>
    )
  const Icon = platform === 'twitter' ? FaXTwitter : platform === 'facebook' ? FaFacebook : null
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center text-[#111827]">
      {Icon ? <Icon className="h-6 w-6" /> : platform[0]?.toUpperCase()}
    </span>
  )
}

/** 645, 4.5K, 87K, 131.9K, 1.3M */
const compact = (n: number | null | undefined) => {
  if (!n) return '—'
  const fmt = (v: number, unit: string) => `${v.toFixed(1).replace(/\.0$/, '')}${unit}`
  if (n >= 1_000_000) return fmt(n / 1_000_000, 'M')
  if (n >= 1_000) return fmt(n / 1_000, 'K')
  return String(Math.round(n))
}
const percent = (r: number | null | undefined) => (r == null ? '—' : `${(r * 100).toFixed(2)}%`)

const SHOWN_PLATFORMS = ['instagram', 'tiktok', 'youtube']

/**
 * One athlete: a big photo (click it for their analytics), their followers on Instagram and
 * TikTok (and YouTube when they have it), total reach and engagement rate.
 */
const AthleteCard = ({
  athlete,
  onClick,
  variant,
}: {
  athlete: PublicTalentSheetAthlete
  onClick: () => void
  variant: SheetVariant
}) => {
  const st = STYLES[variant]
  const initials = athlete.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
  const byPlatform = new Map(athlete.socialAccounts.map((sa) => [sa.platform, sa]))
  // Instagram and TikTok always, so cards line up; YouTube only when they have it
  const platforms = SHOWN_PLATFORMS.filter((p) => p !== 'youtube' || byPlatform.get(p)?.followers)

  return (
    <div className={`flex flex-col gap-4 rounded-2xl border p-4 transition-all ${st.card}`}>
      <button
        type="button"
        onClick={onClick}
        aria-label={`See ${athlete.name}'s analytics`}
        className="group block w-full overflow-hidden rounded-xl"
      >
        {athlete.avatar ? (
          <img
            src={athlete.avatar}
            alt={athlete.name}
            className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <span
            className={`flex aspect-square w-full items-center justify-center text-5xl font-semibold ${st.avatarFallback}`}
          >
            {initials}
          </span>
        )}
      </button>

      <div className="text-center">
        <button type="button" onClick={onClick} className={`text-lg transition-colors ${st.name}`}>
          {athlete.name}
        </button>
        {athlete.school && <p className={`text-xs ${st.muted}`}>{athlete.school}</p>}
      </div>

      <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
        {platforms.map((p) => (
          <div key={p} className="flex items-center gap-2">
            <PlatformMark platform={p} />
            <div className="leading-tight">
              <p className={`font-semibold tabular-nums ${st.title}`}>
                {compact(byPlatform.get(p)?.followers)}
              </p>
              <p className={`text-[11px] ${st.muted}`}>Followers</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 pb-1">
        <div className="flex items-center justify-center gap-2">
          <Flame className={`h-5 w-5 shrink-0 ${st.title}`} strokeWidth={1.75} />
          <div className="leading-tight">
            <p className={`font-semibold tabular-nums ${st.title}`}>
              {compact(athlete.totalReach)}
            </p>
            <p className={`text-[11px] ${st.muted}`}>Total Reach</p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          <TrendingUp className={`h-5 w-5 shrink-0 ${st.title}`} strokeWidth={1.75} />
          <div className="leading-tight">
            <p className={`font-semibold tabular-nums ${st.title}`}>
              {percent(athlete.engagementRate)}
            </p>
            <p className={`text-[11px] ${st.muted}`}>Engagement Rate</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export const TalentSheetLoading = ({ variant = 'dark' }: { variant?: SheetVariant }) => (
  <div className={`${STYLES[variant].page} flex items-center justify-center`}>
    <div
      className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${STYLES[variant].spinner}`}
    />
  </div>
)

export const TalentSheetNotFound = ({ variant = 'dark' }: { variant?: SheetVariant }) => (
  <div className={`${STYLES[variant].page} flex items-center justify-center px-4`}>
    <div className="text-center space-y-2">
      <p className={`text-xl font-semibold ${STYLES[variant].title}`}>Sheet not found</p>
      <p className={`text-sm ${STYLES[variant].empty}`}>
        This talent sheet may have been removed or the link is invalid.
      </p>
    </div>
  </div>
)

interface TalentSheetViewProps {
  sheet: PublicTalentSheet
  onAthleteClick: (athleteId: string) => void
  /** A bar pinned above the header, e.g. the manager's "Back to editing" */
  topBar?: ReactNode
  /** Shown under the athlete grid, e.g. a prompt for brands to get in touch */
  footer?: ReactNode
  /** 'dark' (default) or 'brand', the brand app's light look */
  variant?: SheetVariant
}

export const TalentSheetView = ({
  sheet,
  onAthleteClick,
  topBar,
  footer,
  variant = 'dark',
}: TalentSheetViewProps) => {
  const st = STYLES[variant]
  return (
    <div className={st.page}>
      {topBar && (
        <div className={`sticky top-0 z-50 border-b px-4 py-2 ${st.topBar}`}>{topBar}</div>
      )}

      <div className={`border-b px-4 sm:px-6 py-5 ${st.header}`}>
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <BookOpen className={`h-6 w-6 shrink-0 ${st.icon}`} />
          <div className="flex-1 min-w-0">
            <h1 className={`text-xl font-bold ${st.title}`}>{sheet.title}</h1>
            <p className={`text-xs ${st.muted}`}>
              {sheet.brandName ? `Prepared for ${sheet.brandName} by ` : 'by '}
              {sheet.manager.name}
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {sheet.athletes.length === 0 ? (
          <p className={`text-center py-16 ${st.empty}`}>No athletes on this sheet yet.</p>
        ) : (
          <>
            <p className={`mb-6 text-center text-lg font-medium ${st.title}`}>
              Click creator photo for analytics
            </p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {sheet.athletes.map((athlete) => (
                <AthleteCard
                  key={athlete.id}
                  athlete={athlete}
                  onClick={() => onAthleteClick(athlete.id)}
                  variant={variant}
                />
              ))}
            </div>
          </>
        )}
        {footer}
      </main>

      <footer className={`text-center py-8 text-xs ${st.footer}`}>Powered by Athios</footer>
    </div>
  )
}
